import React, { useEffect, useState, useContext } from 'react';
import { dbPost } from '../../../apis/commonApi';
import DatePicker from '../../../components/dateComponents/DatePicker2';
import CustomChart from '../../../components/chartComponents/customChart';
import { formatDateToISOString } from '../../../services/UtilService';
import { ModalContext } from '../../../components/contexts';

const PageTitle = () => (
    <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">기업 등록 현황</h2>
    </div>
);

const DateFilter = ({
    onDateChange,
    customStartDate,
    customEndDate,
    setCustomStartDate,
    setCustomEndDate,
    selectedPeriod,
    setSelectedPeriod,
}) => {

    const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
    const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
    const { modalService } = useContext(ModalContext);
    const [displayStartDate, setDisplayStartDate] = useState('');
    const [displayEndDate, setDisplayEndDate] = useState('');

    const formatDateForDisplay = date => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const validateDates = (date, comparisonDate, type) => {
        if (type === 'endDate' && !comparisonDate) {
            modalService.errorAlert('먼저 시작 날짜를 선택하세요.');
            return false;
        }

        const selectedDate = new Date(date);
        const compareDate = comparisonDate ? new Date(comparisonDate) : null;

        if (type === 'startDate' && compareDate && selectedDate > compareDate) {
            modalService.errorAlert('시작 날짜는 종료 날짜보다 이후일 수 없습니다.');
            return false;
        }

        if (type === 'endDate' && compareDate && selectedDate < compareDate) {
            modalService.errorAlert('종료 날짜는 시작 날짜보다 이전일 수 없습니다.');
            return false;
        }

        return true;
    };

    const handleStartDateChange = (date) => {
        const formattedDate = formatDateToISOString(date); 
        const displayDate = formatDateForDisplay(date);   
        if (!validateDates(formattedDate, customEndDate, 'startDate')) {
            return;
        }
        setCustomStartDate(formattedDate);
        onDateChange({ startDate: formattedDate, endDate: customEndDate });
        setDisplayStartDate(displayDate);
    };

    const handleEndDateChange = (date) => {
        const formattedDate = formatDateToISOString(date); 
        const displayDate = formatDateForDisplay(date);  
        if (!validateDates(formattedDate, customStartDate, 'endDate')) {
            return;
        }
        setCustomEndDate(formattedDate); 
        onDateChange({ startDate: customStartDate, endDate: formattedDate });
        setDisplayEndDate(displayDate); 
    };

    const handlePeriodSelect = (label) => {
        setSelectedPeriod(label);
        if (label !== '기간 조회') {
            const today = new Date();
            let startDate = null;
            let endDate = new Date();

            if (label === '3개월') {
                startDate = new Date(today.setMonth(today.getMonth() - 3));
            } else if (label === '6개월') {
                startDate = new Date(today.setMonth(today.getMonth() - 6));
            } else if (label === '1년') {
                startDate = new Date(today.setFullYear(today.getFullYear() - 1));
            } else if (label === '2년') {
                startDate = new Date(today.setFullYear(today.getFullYear() - 2));
            } else if (label === '3년') {
                startDate = new Date(today.setFullYear(today.getFullYear() - 3));
            } else if (label === '전체') {
                startDate = null;
                endDate = null;
            }

            const formattedStartDate = formatDateToISOString(startDate);
            const formattedEndDate = formatDateToISOString(endDate, true);

            onDateChange({ startDate: formattedStartDate, endDate: formattedEndDate });
        }
    };

    return (
        <td>
            <div className="input-group">
                {['전체', '3개월', '6개월', '1년', '2년', '3년', '기간 조회'].map((label, idx) => (
                    <div className="form-chip sm" key={idx}>
                        <input
                            type="radio"
                            className="radio"
                            name="rdo_chip_period"
                            id={`rdo_chip_period_${idx}`}
                            checked={selectedPeriod === label}
                            onChange={() => handlePeriodSelect(label)}
                        />
                        <label className="form-chip-outline" htmlFor={`rdo_chip_period_${idx}`} style={{ height: 'auto' }}>
                            {label}
                        </label>
                    </div>
                ))}
            </div>
            {selectedPeriod === '기간 조회' && (
                <div className="input-group gap-md">
                    <DatePicker
                        selectedDate={displayStartDate}
                        onDateChange={handleStartDateChange} 
                        label="시작 날짜"
                    />
                    <DatePicker
                        selectedDate={displayEndDate} 
                        onDateChange={handleEndDateChange} 
                        label="종료 날짜"
                    />
                </div>
            )}
        </td>
    );
};

const ChartSection = ({ graphData, chartOptions }) => {
    const isDataAvailable =
        graphData && graphData.series && graphData.series.some((s) => s.data.length > 0);

    return (
        <div className="tbl-wrap">
            <div className="txt-box bg-white" style={{ height: '600px' }}>
                {isDataAvailable ? (
                    <CustomChart type="line" data={graphData} options={chartOptions} />
                ) : (
                    <p>데이터를 불러오는 중...</p>
                )}
            </div>
        </div>
    );
};

const HorizontalDataTable = ({ title, categories, data }) => {
    const yearlyData = {};
    categories.forEach((category, index) => {
        const year = category.split("-")[0]; 
        if (!yearlyData[year]) {
            yearlyData[year] = { categories: [], data: [] };
        }
        yearlyData[year].categories.push(category);
        yearlyData[year].data.push(data[index]);
    });

    return (
        <>
            <div className="tbl-wrap">
                <h3 className="block-tit">{title}</h3>
                {Object.entries(yearlyData).map(([year, { categories, data }]) => (
                    <table key={year} className="tbl col data scroll">
                        <caption>통계 - 가입자 수 등록 현황 테이블입니다.</caption>
                        <thead>
                            <tr>
                                {categories.map((category, idx) => (
                                    <th scope="col" key={idx}>{category}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {data.map((value, idx) => (
                                    <td key={idx}>{value}</td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                ))}
            </div>
        </>
    );
};


const PerUser = () => {
    useEffect(() => {
            document.title = '통계 - 가입수 등록 현황';
          }, []);

    const [customStartDate, setCustomStartDate] = useState(null);
    const [customEndDate, setCustomEndDate] = useState(null);
    const [graphData, setGraphData] = useState({
        categories: [],
        series: [
            { name: '기업 등록 수', data: [] },
            { name: '사용자 수', data: [] },
        ],
    });
    const [chartOptions, setChartOptions] = useState({
        chart: { width: '100%', height: '600px', title: '기업등록현황' },
        xAxis: { title: '월', categories: [] },
        yAxis: { title: '가입 수' },
        series: { showLabel: true },
    });
    const [selectedPeriod, setSelectedPeriod] = useState('전체');

    const fetchChartData = async (filter) => {
        try {
            const response = await dbPost('/stats/entStats/user/search.do', filter);
            if (response && response.length > 0) {
                const chartData = response.map((item) => ({
                    month: item.month || '',
                    registered: item.memberEnt || 0,
                    users: item.memberUser || 0,
                }));

                setGraphData({
                    categories: chartData.map((item) => item.month),
                    series: [
                        { name: '기업 등록 수', data: chartData.map((item) => item.registered) },
                        { name: '사용자 수', data: chartData.map((item) => item.users) },
                    ],
                });

                setChartOptions((prev) => ({
                    ...prev,
                    xAxis: { ...prev.xAxis, categories: chartData.map((item) => item.month) },
                }));
            } else {
                setGraphData({
                    categories: [],
                    series: [
                        { name: '기업 등록 수', data: [] },
                        { name: '사용자 수', data: [] },
                    ],
                });
            }
        } catch (error) {
        }
    };

    useEffect(() => {
        fetchChartData({ startDate: null, endDate: null });
    }, []);

    return (
        <div className="contents">
            <PageTitle />
            <div className="tbl-wrap">
                <table className="tbl data row">
                    <caption>조회 기간 검색을 위한 테이블입니다.</caption>
                    <tbody>
                        <tr>
                            <th scope="row">조회 기간</th>
                            <DateFilter
                                onDateChange={fetchChartData}
                                customStartDate={customStartDate}
                                customEndDate={customEndDate}
                                setCustomStartDate={setCustomStartDate}
                                setCustomEndDate={setCustomEndDate}
                                selectedPeriod={selectedPeriod}
                                setSelectedPeriod={setSelectedPeriod}
                            />
                        </tr>
                    </tbody>
                </table>
            </div>
            <ChartSection graphData={graphData} chartOptions={chartOptions} />
            <HorizontalDataTable
                title="월별 기업 등록 수"
                categories={graphData.categories}
                data={graphData.series[0]?.data || []}
            />
            <HorizontalDataTable
                title="월별 사용자 수"
                categories={graphData.categories}
                data={graphData.series[1]?.data || []}
            />
        </div>
    );
};

export default PerUser;
